import { useEffect, useState } from "react";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel } from "@/lib/useReportRange";
import { exportExcel, exportPDF } from "@/lib/exporters";

function Block({ title, d }) {
  return (
    <div className="border border-gray-200 rounded bg-white">
      <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wider text-gray-600 font-semibold">{title}</div>
      <div className="p-4 grid grid-cols-2 gap-3 text-sm">
        <div><div className="text-xs text-gray-500 uppercase tracking-wider">Taxable</div><div className="font-mono">₹ {formatINR(d.taxable)}</div></div>
        <div><div className="text-xs text-gray-500 uppercase tracking-wider">CGST</div><div className="font-mono">₹ {formatINR(d.cgst)}</div></div>
        <div><div className="text-xs text-gray-500 uppercase tracking-wider">SGST</div><div className="font-mono">₹ {formatINR(d.sgst)}</div></div>
        <div><div className="text-xs text-gray-500 uppercase tracking-wider">IGST</div><div className="font-mono">₹ {formatINR(d.igst)}</div></div>
        <div className="col-span-2 pt-2 border-t border-gray-100">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Total Tax</div>
          <div className="font-mono text-lg font-semibold">₹ {formatINR(d.total_tax)}</div>
        </div>
      </div>
    </div>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/companies/${activeId}/reports/gst-summary`, { params }).then((r) => setData(r.data));
  }, [activeId, applied.start, applied.end, params]);

  const onExcel = () => {
    if (!data) return;
    const rows = (label, d) => [[label], ["Taxable", d.taxable], ["CGST", d.cgst], ["SGST", d.sgst], ["IGST", d.igst], ["Total Tax", d.total_tax], []];
    exportExcel(`gst-summary-${active?.name || "report"}`, [{
      name: "GST Summary",
      rows: [
        [`Period: ${formatRangeLabel(range)}`], [],
        ...rows("Output GST (Sales)", data.output),
        ...rows("Input GST (Purchases)", data.input),
        ["Net Payable", data.net_payable],
      ],
    }]);
  };
  const onPDF = () => {
    if (!data) return;
    const rows = (d) => [["Taxable", formatINR(d.taxable)], ["CGST", formatINR(d.cgst)], ["SGST", formatINR(d.sgst)], ["IGST", formatINR(d.igst)], ["Total Tax", formatINR(d.total_tax)]];
    exportPDF(`gst-summary-${active?.name || "report"}`, "GST Summary", [
      { title: `Period: ${formatRangeLabel(range)} — Output GST (Sales)`, headers: ["Item", "Amount (₹)"], rows: rows(data.output) },
      { title: "Input GST (Purchases)", headers: ["Item", "Amount (₹)"], rows: rows(data.input) },
      { title: "Net", headers: ["", "Amount (₹)"], rows: [[data.net_payable >= 0 ? "Net Payable" : "Net Input Credit", formatINR(Math.abs(data.net_payable))]] },
    ], { company: active?.name });
  };

  return (
    <>
      <PageHeader title="GST Summary" subtitle={`${active?.name || ""}  •  ${formatRangeLabel(range)}`}
        actions={<ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />}
      />
      <div className="p-6 space-y-4" data-testid="gst-summary">
        <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
        {data && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Block title="Output GST (Sales)" d={data.output} />
              <Block title="Input GST (Purchases)" d={data.input} />
            </div>
            <div className={`border rounded p-4 text-right font-mono text-xl font-semibold ${data.net_payable >= 0 ? "border-red-200 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
              {data.net_payable >= 0 ? "Net GST Payable" : "Net Input Credit"}: ₹ {formatINR(Math.abs(data.net_payable))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
export default function GSTSummary() { return <RequireCompany><Inner /></RequireCompany>; }
